import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useScopedRoster, type ScopedRosterMember } from '@/hooks/useScopedRoster';
import type { SortOrder } from '@/hooks/useRoster';
import type { RosterSidebarOrg } from './sidebar/RosterSidebar';

function initials(member: ScopedRosterMember): string {
  const name = member.displayName || 'Unknown';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function MemberRow({ member, onClick }: { member: ScopedRosterMember; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-flock-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-flock-navy-300"
    >
      {member.profileImageUrl ? (
        <img src={member.profileImageUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-flock-navy-400 text-sm font-semibold text-white">
          {initials(member)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-flock-navy-900">
          {member.displayName || 'Unknown'}
        </div>
        <div className="truncate text-xs text-flock-navy-400">{member.email}</div>
      </div>
      {member.phone && (
        <span className="shrink-0 text-xs text-flock-navy-400">{member.phone}</span>
      )}
    </button>
  );
}

/**
 * Org-level roster pane ("All Members") for leadership roles. The backend
 * scopes it automatically: org_admin sees the whole org, campus_pastor their
 * campus, ministry_director their ministry. Simpler columns than the group
 * roster — the scoped endpoint carries no health/attendance data.
 */
export default function OrgRosterPage({ org }: { org: RosterSidebarOrg }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOrder>('name_asc');

  const rosterQuery = useScopedRoster(org.orgId, {
    ...(search.trim() ? { search: search.trim() } : {}),
    sort,
  });
  const pages = rosterQuery.data?.pages ?? [];
  const members = pages.flatMap((p) => p.members);
  const total = pages[0]?.total ?? 0;
  const scopeLabel = pages[0]?.scopeLabel || org.orgName;

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-flock-gray-200 bg-white px-6 py-4">
        <h1 className="text-lg font-semibold text-flock-navy-900">All Members</h1>
        <p className="text-xs text-flock-navy-400">
          {scopeLabel}
          {total > 0 ? ` · ${total} members` : ''}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-300" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              className="w-64 rounded-lg border border-flock-gray-200 bg-flock-navy-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-flock-navy-400"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOrder)}
            className="rounded-lg border border-flock-gray-200 bg-white px-2 py-2 text-sm text-flock-navy-700"
          >
            <option value="name_asc">Name A→Z</option>
            <option value="name_desc">Name Z→A</option>
          </select>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-2">
          {rosterQuery.isLoading && (
            <div className="py-12 text-center text-sm text-flock-navy-400">Loading members…</div>
          )}
          {rosterQuery.isError && (
            <div className="py-4 text-center text-sm text-red-600">
              Couldn&apos;t load the member list. Try again?
            </div>
          )}
          {!rosterQuery.isLoading && !rosterQuery.isError && members.length === 0 && (
            <div className="py-12 text-center text-sm text-flock-navy-400">
              {search ? 'No members match your search.' : 'No members yet.'}
            </div>
          )}
          {members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              onClick={() => navigate(`/roster/org/${org.orgId}/member/${member.id}`)}
            />
          ))}
          {rosterQuery.hasNextPage && (
            <button
              onClick={() => rosterQuery.fetchNextPage()}
              disabled={rosterQuery.isFetchingNextPage}
              className="w-full rounded-xl border border-flock-gray-200 bg-white py-2.5 text-sm font-medium text-flock-navy-600 transition hover:border-flock-navy-300 disabled:opacity-60"
            >
              {rosterQuery.isFetchingNextPage
                ? 'Loading…'
                : `Load more (${members.length} of ${total})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
